import { useEffect, useLayoutEffect, useMemo } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { Team } from '@ardoq/profiling';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import { recordViewModelReady } from 'tracking/durationTracking/viewPerformance';
import { RelationshipsView } from './RelationshipsView';
import {
  EMPTY_INTERACTIVE_RELATIONSHIPS_VIEW_LEGEND_STATE,
  type InteractiveRelationshipsViewLegendState,
} from 'tabview/relationshipDiagrams/interactiveRelationshipsViewLegendState$';
import {
  buildRelationshipsViewRenderingData,
  type BuildRelationshipsViewRenderingDataSourceArgs,
} from './viewModel$/buildRelationshipsViewRenderingData';
import { toProps } from './viewModel$/toProps';
import { getPerformanceTrackingData } from './viewModel$/performanceTrackingUtils';
import type {
  UiPropsSettings,
  ViewSettingsForRenderingData,
} from './viewModel$/viewSettingsPartitions';
import type {
  RelationshipsViewCommands,
  RelationshipsViewProperties,
} from './types';
import { toRelationshipsViewDependencies } from './viewpointMode/settingsControls';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

const PerformanceTrackedRelationshipsView = (
  props: RelationshipsViewProperties
) => {
  usePerformanceTracking('hierarchical relationships view render', 5000, {
    viewId: VIEW_ID,
    team: Team.INSIGHT,
    metadata: {
      linkCount: props.links.length,
    },
  });

  return <RelationshipsView {...props} />;
};

export type RelationshipsRenderingDataLayerStaticProps = {
  isViewpointMode: boolean;
  relationshipsViewCommands: RelationshipsViewCommands;
  viewStatusReporter: ViewStatusReporter;
};

export type RelationshipsRenderingDataLayerStreamedProps = {
  sourceData: BuildRelationshipsViewRenderingDataSourceArgs;
  /** Data work the view finishes once it has rendered this source data; null where nothing reports it. */
  dataWorkToken: string | null;
  viewSettingsForRenderingData: ViewSettingsForRenderingData;
  uiPropsSettings: UiPropsSettings;
  /** Supplied by the connected path; an embedded view has no legend state of its own. */
  interactiveLegendState?: InteractiveRelationshipsViewLegendState;
};

export type RelationshipsRenderingDataLayerProps =
  RelationshipsRenderingDataLayerStaticProps &
    RelationshipsRenderingDataLayerStreamedProps;

const RelationshipsRenderingDataLayer = ({
  isViewpointMode,
  relationshipsViewCommands,
  viewStatusReporter,
  sourceData,
  dataWorkToken,
  viewSettingsForRenderingData,
  uiPropsSettings,
  interactiveLegendState = EMPTY_INTERACTIVE_RELATIONSHIPS_VIEW_LEGEND_STATE,
}: RelationshipsRenderingDataLayerProps) => {
  const renderingData = useMemo(
    () => buildRelationshipsViewRenderingData(sourceData),
    [sourceData]
  );

  const mappedProperties = useMemo(
    () =>
      toProps({
        renderingData,
        renderingDataSettings: viewSettingsForRenderingData,
        interactiveRelationshipsViewLegendState: interactiveLegendState,
        isViewpointMode,
      }),
    [
      renderingData,
      viewSettingsForRenderingData,
      interactiveLegendState,
      isViewpointMode,
    ]
  );

  useLayoutEffect(() => {
    if (mappedProperties.performanceTrackingId) {
      recordViewModelReady(
        mappedProperties.performanceTrackingId,
        getPerformanceTrackingData(mappedProperties)
      );
    }
  }, [mappedProperties]);

  useEffect(() => {
    if (dataWorkToken) {
      viewStatusReporter.reportWorkFinished(dataWorkToken);
    }
  }, [viewStatusReporter, dataWorkToken]);

  const viewProperties: RelationshipsViewProperties = useMemo(
    () => ({
      ...mappedProperties,
      viewSettings: { ...mappedProperties.viewSettings, ...uiPropsSettings },
      relationshipsViewCommands,
      viewStatusReporter,
    }),
    [
      mappedProperties,
      uiPropsSettings,
      relationshipsViewCommands,
      viewStatusReporter,
    ]
  );

  useEffect(() => {
    if (isViewpointMode) {
      relationshipsViewCommands.updateViewProperties(
        toRelationshipsViewDependencies(viewProperties)
      );
    }
    relationshipsViewCommands.updateViewEmptyStateData(
      viewProperties.isViewEmpty
    );
  }, [viewProperties, relationshipsViewCommands, isViewpointMode]);

  return (
    <WithLoadingIndicator
      viewInstanceId={viewProperties.viewInstanceId}
      loadingSource="activeViewsStatus"
      showContentWhileLoading
      i18nLocale={viewProperties.i18nLocale}
    >
      <PerformanceTrackedRelationshipsView {...viewProperties} />
    </WithLoadingIndicator>
  );
};

export { RelationshipsRenderingDataLayer };
